import React, { useEffect, useState } from "react";
import { ReactComponent as EditIcon } from "../../Global Icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../../Global Icons/Delete.svg";
import { ReactComponent as CloseIcon } from "../../Global Icons/CancelButton.svg";
import TextInput from "../../Global Functions/textInput";
import SelectInput from "../../Global Functions/selectInput";
import { useDispatch, useSelector } from "react-redux";
import { deleteAR, updateAR } from "../../Redux/Actions/propertiesResaleActions";
import { viewProperty } from "../../Redux/Actions/propertyActions"; 
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AddImage from "../../Global Functions/addImage";
import "./addAr.css";

const AddAr = ({ arData, onArRemoved, onClose }) => {
    const [formData, setFormData] = useState({
        type: "url",
        contentType: "",
        iframe_url: "",
    });

    const [arItems, setArItems] = useState({
        id: arData?.data?._id || "",
        ar_items: [],
    });

    const [editingIndex, setEditingIndex] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [videoUploading, setVideoUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentVideoUrl, setCurrentVideoUrl] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ✅ Step 1: Read fresh property from Redux — same pattern as AddVr
    const { property, loading: propertyLoading } = useSelector(
        (state) => state.properties || {}
    );
    const localProperty = property?.data;

    // ✅ Step 2: Fetch full property on mount — so we always get latest DB data
    useEffect(() => {
        const propertyId = arData?.data?._id;
        if (propertyId) {
            dispatch(viewProperty(propertyId));
        }
    }, [arData?.data?._id, dispatch]);

    // ✅ Step 3: Whenever Redux property updates, seed ar_items from property_ar
    //    This is the fix — previously relying only on arData prop meant stale/empty data
    useEffect(() => {
        if (!localProperty || localProperty._id !== arData?.data?._id) return;

        setArItems({
            id: localProperty._id,
            ar_items: Array.isArray(localProperty.property_ar)
                ? localProperty.property_ar
                : [],
        });
    }, [localProperty, arData?.data?._id]);

    const validVideoTypes = ["video/mp4", "video/avi", "video/mov", "video/wmv", "video/mkv", "video/webm"];

    const handleMediaUpload = async (event) => {
        const uploadedFile = event.target.files[0];
        if (!uploadedFile) { toast.error("No File Uploaded"); return; }
        if (!validVideoTypes.includes(uploadedFile.type)) {
            toast.error("Only video files are allowed!");
            event.target.value = "";
            return;
        }

        setCurrentVideoUrl("");
        setFormData(prev => ({ ...prev, contentType: uploadedFile.type }));
        setVideoUploading(true);
        setUploadProgress(0);

        const progressInterval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 90) { clearInterval(progressInterval); return 90; }
                return prev + 10;
            });
        }, 300);

        try {
            const responseUrl = await AddImage(uploadedFile);
            clearInterval(progressInterval);
            setUploadProgress(100);
            if (responseUrl) { setCurrentVideoUrl(responseUrl); event.target.value = ""; }
        } catch (error) {
            toast.error("Video upload failed!");
            setFormData(prev => ({ ...prev, contentType: "" }));
        } finally {
            setVideoUploading(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    };

    const handleAddEntry = (event) => {
        event.preventDefault();
        const { type, contentType, iframe_url } = formData;
        if (!type || !contentType || (type === "url" && !iframe_url.trim()) || (type === "video" && !currentVideoUrl)) {
            return toast.error("All fields are required!");
        }
        const newItem = {
            _tempId: Date.now() + Math.random(),
            type,
            type_data: [{
                type: contentType,
                ...(type === "video" ? { video: currentVideoUrl, iframe_url: "" } : { iframe_url, video: "" }),
            }],
        };
        setArItems((prev) => ({ ...prev, ar_items: [...prev.ar_items, newItem] }));
        setFormData({ type: "url", contentType: "", iframe_url: "" });
        setCurrentVideoUrl("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (arItems.ar_items.length === 0) return toast.error("Please Add AR content using ADD Button!");
        try {
            const response = await dispatch(updateAR({ id: arItems.id, ar_items: arItems.ar_items }));
            if (response?.statusCode === 200) {
                dispatch(viewProperty(arItems.id)); // ✅ re-fetch so list is fresh next time
                if (typeof onArRemoved === "function") onArRemoved();
                navigate("/properties_types");
            }
        } catch (error) {
            toast.error("Failed to submit AR content");
        }
    };

    const handleEdit = (itemIndex) => {
        const itemToEdit = arItems.ar_items[itemIndex];
        if (!itemToEdit) return;
        setEditingIndex(itemIndex);
        setEditingItem(itemToEdit);
        setFormData({
            type: itemToEdit.type,
            contentType: itemToEdit.type_data?.[0]?.type || "",
            iframe_url: itemToEdit.type_data?.[0]?.iframe_url || "",
        });
        if (itemToEdit.type === "video" && itemToEdit.type_data?.[0]?.video) {
            setCurrentVideoUrl(itemToEdit.type_data[0].video);
        }
        setTimeout(() => {
            document.querySelector('.ar-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
    };

    const handleUpdate = async (event) => {
        event.preventDefault();
        const { type, contentType, iframe_url } = formData;
        if (!type || !contentType || (type === "url" && !iframe_url.trim()) || (type === "video" && !currentVideoUrl)) {
            return toast.error("All fields are required!");
        }
        const updatedItem = {
            type,
            type_data: [{
                type: contentType,
                ...(type === "video" ? { video: currentVideoUrl, iframe_url: "" } : { iframe_url, video: "" }),
            }],
        };
        const updatedList = arItems.ar_items.map((item, index) =>
            index === editingIndex ? { ...item, ...updatedItem } : item
        );

        if (editingItem?._id) {
            try {
                const response = await dispatch(updateAR({ id: arItems.id, ar_items: updatedList }));
                if (response?.statusCode === 200) {
                    dispatch(viewProperty(arItems.id)); // ✅ re-fetch
                    if (typeof onArRemoved === "function") onArRemoved();
                    resetForm();
                }
            } catch (error) { toast.error("Error updating AR item"); }
        } else {
            setArItems((prev) => ({ ...prev, ar_items: updatedList }));
            toast.success("AR item updated!");
            resetForm();
        }
    };

    const handleCancelEdit = () => { resetForm(); toast.success("Edit cancelled"); };

    const resetForm = () => {
        setFormData({ type: "url", contentType: "", iframe_url: "" });
        setCurrentVideoUrl("");
        setEditingIndex(null);
        setEditingItem(null);
    };

    const handleRemove = async (itemIndex) => {
        const itemToRemove = arItems.ar_items[itemIndex];
        if (!itemToRemove) return;
        if (editingIndex === itemIndex) resetForm();

        if (!itemToRemove._id) {
            setArItems((prev) => ({ ...prev, ar_items: prev.ar_items.filter((_, i) => i !== itemIndex) }));
            toast.success("AR item removed!");
            return;
        }
        try {
            const response = await dispatch(deleteAR({ id: arItems.id, ar_id: itemToRemove._id }));
            if (response?.statusCode === 200) {
                dispatch(viewProperty(arItems.id)); // ✅ re-fetch after delete
                toast.success("AR item removed successfully!");
                if (typeof onArRemoved === "function") onArRemoved();
            }
        } catch (error) { toast.error("Error removing AR item"); }
    };

    const handleClearAll = () => {
        resetForm();
        if (typeof onClose === "function") onClose();
    };

    const handleRemoveVideo = () => {
        setCurrentVideoUrl("");
        setFormData(prev => ({ ...prev, contentType: "" }));
    };

    const isFetching = propertyLoading && !localProperty;

    return (
        <div className="ar-modal-container">
            <div className="ar-modal-header">
                <h2 className="ar-modal-title">Add AR Content</h2>
                <CloseIcon className="ar-close-icon" onClick={handleClearAll} title="Close" />
            </div>

            <div className="ar-modal-content">
                {isFetching ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <p style={{ fontSize: "13px", fontWeight: 600 }}>Loading property data...</p>
                    </div>
                ) : (
                    <>
                        {/* ✅ Already-added AR items from DB render here */}
                        {arItems.ar_items?.length > 0 && (
                            <div className="ar-list-section">
                                <h3 className="ar-list-heading">Added AR Content</h3>
                                <div className="ar-list-container">
                                    {arItems.ar_items.map((item, index) => (
                                        <div
                                            key={item?._id || item?._tempId || `ar-${index}`}
                                            className={`ar-list-item ${editingIndex === index ? 'editing-highlight' : ''}`}
                                        >
                                            <div className="ar-item-content">
                                                <h4 className="ar-item-title">
                                                    {item?.type_data?.[0]?.type || 'Unknown'} ({item?.type || 'N/A'})
                                                    {editingIndex === index && <span className="editing-badge">Editing</span>}
                                                </h4>
                                                {item?.type === "url" && item?.type_data?.[0]?.iframe_url && (
                                                    <p className="ar-item-url">{item.type_data[0].iframe_url}</p>
                                                )}
                                                {item?.type === "video" && item?.type_data?.[0]?.video && (
                                                    <div className="ar-video-preview">
                                                        <video className="ar-video-player" controls preload="metadata">
                                                            <source src={item.type_data[0].video} type={item.type_data[0].type || "video/mp4"} />
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ar-item-actions">
                                                <button
                                                    className="ar-action-btn ar-edit-btn"
                                                    title="Edit"
                                                    onClick={() => handleEdit(index)}
                                                    disabled={editingIndex !== null && editingIndex !== index}
                                                >
                                                    <EditIcon />
                                                </button>
                                                <button
                                                    className="ar-action-btn ar-delete-btn"
                                                    onClick={() => handleRemove(index)}
                                                    title="Delete"
                                                    disabled={editingIndex === index}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="ar-form-section">
                            <h3 className="ar-form-heading">
                                {editingIndex !== null ? "Edit AR Content"
                                    : arItems.ar_items?.length > 0 ? "Add Another"
                                    : "Add New AR Content"}
                            </h3>
                            <div className="ar-form-grid">
                                <SelectInput
                                    label="Content Type"
                                    required={true}
                                    value={formData.type}
                                    placeholder="Select Type"
                                    options={[
                                        { value: "url", label: "AR URL" },
                                        { value: "video", label: "Video" },
                                    ]}
                                    onChange={(e) => {
                                        setFormData((prev) => ({ ...prev, type: e.target.value, iframe_url: "", contentType: "" }));
                                        setCurrentVideoUrl("");
                                    }}
                                />

                                {formData.type === "url" && (
                                    <TextInput
                                        label="Content Type"
                                        required={true}
                                        value={formData.contentType}
                                        placeholder="Enter content type (model/floorplan/other)"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, contentType: e.target.value.trim() }))}
                                    />
                                )}

                                {formData.type === "url" && (
                                    <TextInput
                                        label="Iframe URL"
                                        required={true}
                                        value={formData.iframe_url}
                                        placeholder="https://example.com/..."
                                        onChange={(e) => setFormData((prev) => ({ ...prev, iframe_url: e.target.value }))}
                                    />
                                )}

                                {formData.type === "video" && (
                                    <div>
                                        <span>Upload Video</span>
                                        <input
                                            className="input-class"
                                            type="file"
                                            accept="video/mp4,video/avi,video/mov,video/wmv,video/mkv,video/webm"
                                            onChange={handleMediaUpload}
                                            style={{ display: "none" }}
                                            id="ar-video-upload"
                                        />
                                        <label className="label-class-images" htmlFor="ar-video-upload">
                                            Click to Upload Video
                                        </label>

                                        {videoUploading && (
                                            <div style={{ marginTop: '10px' }}>
                                                <div style={{ color: '#666', marginBottom: '5px' }}>Uploading video... {uploadProgress}%</div>
                                                <div style={{ width: '100%', height: '20px', backgroundColor: '#e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${uploadProgress}%`, height: '100%',
                                                        backgroundColor: uploadProgress === 100 ? '#4CAF50' : '#2196F3',
                                                        transition: 'width 0.3s ease',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: 'white', fontSize: '12px', fontWeight: 'bold'
                                                    }}>
                                                        {uploadProgress > 0 && `${uploadProgress}%`}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {currentVideoUrl && formData.contentType && (
                                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '5px' }}>
                                                <strong>Selected Type:</strong> {formData.contentType.split('/')[1]?.toUpperCase() || 'VIDEO'}
                                            </div>
                                        )}

                                        {currentVideoUrl && (
                                            <div style={{ position: 'relative', marginTop: '10px', width: '100%', maxWidth: '300px' }}>
                                                <video width="100%" height="auto" controls style={{ borderRadius: '8px' }}>
                                                    <source src={currentVideoUrl} type={formData.contentType || "video/mp4"} />
                                                    Your browser does not support the video tag.
                                                </video>
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveVideo}
                                                    style={{
                                                        position: 'absolute', top: '8px', right: '8px',
                                                        width: '28px', height: '28px',
                                                        backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                                                        border: 'none', borderRadius: '50%', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '18px', fontWeight: 'bold',
                                                    }}
                                                    title="Remove video"
                                                >×</button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {editingIndex !== null ? (
                                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                                        <button className="ar-add-btn" onClick={handleUpdate} style={{ flex: 1 }}>Update</button>
                                        <button className="ar-cancel-btn" onClick={handleCancelEdit} style={{ flex: 1 }}>Cancel</button>
                                    </div>
                                ) : (
                                    (formData.iframe_url.trim().length > 0 || currentVideoUrl) && (
                                        <button className="ar-add-btn" onClick={handleAddEntry}>Add</button>
                                    )
                                )}
                            </div>
                        </div>

                        {arItems.ar_items?.length > 0 && editingIndex === null && (
                            <div className="ar-submit-section">
                                <button className="ar-submit-btn" onClick={handleSubmit}>Submit All</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AddAr;